import {
	useState,
	useMemo,
	Dispatch,
	SetStateAction,
	useImperativeHandle,
	forwardRef,
	useRef,
	useEffect,
	useCallback,
	ReactNode,
} from 'react';
import {
	Table,
	Button,
	InputNumber,
	Select,
	Input,
	Tooltip,
	Modal,
	Space,
	Popconfirm,
	message,
	Tag,
	Switch,
} from 'antd';
import {
	DeleteOutlined,
	CopyOutlined,
	MenuOutlined,
	VerticalAlignBottomOutlined,
	InfoCircleOutlined,
	CheckOutlined,
	OpenAIOutlined,
} from '@ant-design/icons';
import { useGetGageTypesQuery, useGetKeyenceSystemsQuery } from '../../redux/api/quality';
import { Balloon } from '../../interfaces/Balloon';
import DeleteBalloonsModal from '../DeleteBalloonsModal';
import NominalInput from '../NominalInput';
import {
	countUnreviewed,
	isUnreviewedAutoBalloon,
	markBulkEdit,
	markIndividualEdit,
	stripAutoGeneration,
} from '../../utils/balloonReview';
import { ToleranceTable } from '../../interfaces/Ballooning';
import {
	applyNominalEdit,
	describeRow,
	hasRows,
	TABLE_SOURCE_LABELS,
} from '../../utils/toleranceTable';
import {
	isFieldDisabled,
	isKeyenceStrategy,
	MeasurementField,
	reachableKeyenceSystems,
	StrategyOption,
	strategyOptions as buildStrategyOptions,
	strategyUpdates,
	strategyValue,
	toolField,
	toolUpdates,
} from './measurementFields';

interface BalloonTableProps {
	activeBalloonId: string | null;
	setActiveBalloonId: (id: string | null) => void;
	setCenterOnActive: (value: boolean) => void;
	localBalloons: Balloon[];
	setLocalBalloons: (balloons: Balloon[]) => void;
	setDeletedBalloonIds: (ids: string[]) => void;
	scrollY: number;
	pageNumber: number | null;
	setPageNumber: Dispatch<SetStateAction<number>>;
	onValidate?: (isValid: boolean) => void;
	editable?: boolean;
	disableDragging?: boolean;
	/** Fills tolerances for dimensions typed without one. */
	toleranceTable?: ToleranceTable | null;
	allowedEditableBalloonId?: string | null;
	onRow?: (record: Balloon) => any;
	toolbarLeft?: ReactNode;
	/** Where this part will be worked, when material has already fixed it.
	 *  Restricts the Keyence picker to devices that are actually reachable
	 *  for it (PTB-6816). Null leaves the picker open. */
	keyenceBuildingId?: string | null;
	/** Marks auto-generated balloons as reviewed on the server. */
	onApproveBalloons?: (ids: string[]) => Promise<unknown>;
}



interface GageType {
	id: string;
	name: string;
	category: {
		id: string;
		name: string;
	};
	is_fastlane_gage: boolean;
}

type ValidationError = {
	balloonId: string;
	field: keyof Balloon;
	message: string;
};

// Helper: Renumber balloons sequentially starting at 1
const renumberBalloons = (balloons: Balloon[]) =>
	balloons.map((balloon, index) => ({
		...balloon,
		balloon_number: index + 1,
	}));

const BalloonTable = forwardRef<any, BalloonTableProps>((props, ref) => {
	const {
		activeBalloonId,
		setActiveBalloonId,
		setCenterOnActive,
		localBalloons,
		setLocalBalloons,
		setDeletedBalloonIds,
		scrollY,
		setPageNumber,
		editable = true,
		disableDragging = false,
		allowedEditableBalloonId,
		toleranceTable,
		toolbarLeft,
		keyenceBuildingId = null,
		onApproveBalloons,
	} = props;

	const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
	const [approvingIds, setApprovingIds] = useState<string[]>([]);

	const [confirmModalVisible, setConfirmModalVisible] = useState(false);
	const [pendingDeletionRecord, setPendingDeletionRecord] = useState<Balloon[]>(
		[]
	);

	const [selectedRowKeys, setSelectedRowKeysState] = useState<React.Key[]>([]);
	// Selection only feeds bulk edits, so a read-only table (e.g. completed) selects nothing.
	const setSelectedRowKeys = useCallback(
		(keys: React.Key[]) => setSelectedRowKeysState(editable ? keys : []),
		[editable]
	);
	useEffect(() => {
		if (!editable) setSelectedRowKeysState([]);
	}, [editable]);
	const selectionAnchorRef = useRef<string | null>(null);
	const isShiftPressed = useRef(false);

	const [focusedSelectId, setFocusedSelectId] = useState<string | null>(null);

	const [draggedRow, setDraggedRow] = useState<Balloon | null>(null);
	const [hoveredRow, setHoveredRow] = useState<string | null>(null);

	const [focusedCell, setFocusedCell] = useState<{
		rowId: string;
		columnKey: string;
	} | null>(null);
	const cellRefs = useRef<
		Record<
			string,
			Record<
				string,
				HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
			>
		>
	>({});

	// API queries
	const { data: gageAllTypes } = useGetGageTypesQuery({});
	const { data: allKeyenceSystems } = useGetKeyenceSystemsQuery();

	// A part is inspected at one site, so only offer machines that are actually
	// there (PTB-6816).
	const keyenceSystems = useMemo(
		() => reachableKeyenceSystems(allKeyenceSystems, keyenceBuildingId),
		[allKeyenceSystems, keyenceBuildingId]
	);

	// The body scrolls inside the space left under the toolbar and the column headers.
	const tableAreaRef = useRef<HTMLDivElement>(null);
	const [tableBodyHeight, setTableBodyHeight] = useState<number>();
	useEffect(() => {
		const area = tableAreaRef.current;
		if (!area) return;
		const measure = () => {
			const header = area.querySelector<HTMLElement>('.ant-table-header');
			setTableBodyHeight(Math.max(80, area.clientHeight - (header?.offsetHeight ?? 39)));
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(area);
		return () => observer.disconnect();
	}, []);

	// Memoized calculations
	const fastlaneGages = useMemo(
		() => gageAllTypes?.filter((gage) => gage.is_fastlane_gage) || [],
		[gageAllTypes]
	);

	const gageTypeMap = useMemo<Record<string, GageType>>(
		() =>
			fastlaneGages.reduce<Record<string, GageType>>((acc, gage) => {
				acc[gage.id] = gage;
				return acc;
			}, {}),
		[fastlaneGages]
	);



	const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
		[]
	);

	const lastInteractionWasKeyboard = useRef(false);

	const registerCellRef = useCallback(
		(rowId: string, columnKey: string, ref: any) => {
			if (!cellRefs.current[rowId]) cellRefs.current[rowId] = {};
			cellRefs.current[rowId][columnKey] = ref;
		},
		[]
	);

	useImperativeHandle(ref, () => ({
		// Only the given rows when ids is passed (an approval saves just those).
		validate: (ids?: string[]) => {
			const allErrors = localBalloons
				.filter((balloon) => !ids || ids.includes(balloon.id as string))
				.map((balloon) => validateBalloon(balloon))
				.flat();

			setValidationErrors(allErrors);
			return allErrors.length === 0;
		},
		// Marks the cells completion is waiting on (see missingFields).
		showMissingFields: (missing: { balloonId: string; field: keyof Balloon; label: string }[]) => {
			setValidationErrors(
				missing.map(({ balloonId, field, label }) => ({
					balloonId,
					field,
					message: `${label} is required`,
				}))
			);
		},
		// Same flow as the row's delete button, including the renumber prompt.
		deleteBalloon: (id: string) => {
			const record = localBalloons.find((b) => b.id === id);
			if (record) handleDeleteBalloon(record);
		},
		// The row's Strategy and Tool cells, for editors outside the table (the quick edit).
		changeStrategy: (id: string, value: string, option: StrategyOption) => {
			const record = localBalloons.find((b) => b.id === id);
			if (record) handleStrategyChange(record, value, option);
		},
		changeTool: (id: string, value: string) => {
			const record = localBalloons.find((b) => b.id === id);
			if (record) handleToolChange(record, value);
		},
	}));

	useEffect(() => {
		const handleKeyDown = () => {
			lastInteractionWasKeyboard.current = true;
		};
		const handleMouseDown = () => {
			lastInteractionWasKeyboard.current = false;
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('mousedown', handleMouseDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('mousedown', handleMouseDown);
		};
	}, []);

	// Applies updates and clears the fields the new strategy/type makes meaningless.
	const applyBalloonUpdates = (
		balloon: Balloon,
		updates: Partial<Balloon>
	): Balloon => {
		const updated: Balloon = { ...balloon, ...updates };
		const category = gageTypeMap[updated.gage_type]?.category?.name;

		// Check if measurement_strategy changed
		if (updates.measurement_strategy) {
			if (updated.measurement_strategy === 'Visual') {
				updated.measurement_type = 'Engraving';
			} else if (
				updated.measurement_strategy !== 'Visual' &&
				updated.measurement_type === 'Engraving'
			) {
				updated.measurement_type = null;
			}
		}

		if (updates.nominal !== undefined && updates.nominal !== null) {
			updated.text_value = null;
		}
		if (updates.text_value !== undefined && updates.text_value !== '') {
			updated.nominal = null;
		}

		// Auto-clean dependent fields
		const cleanableFields = {
			view: !isKeyenceStrategy(updated.measurement_strategy),
			measurement_type:
				isKeyenceStrategy(updated.measurement_strategy) ||
				updated.measurement_strategy === 'Hand Gage',
			gage_type:
				isKeyenceStrategy(updated.measurement_strategy) ||
				updated.measurement_strategy === 'Visual' ||
				updated.measurement_type === 'Engraving',
			nominal:
				updated.measurement_type === 'Engraving' ||
				category === 'Thread Gages',
			tolerance_minus:
				updated.measurement_type === 'Engraving' ||
				category === 'Thread Gages',
			tolerance_plus:
				updated.measurement_type === 'Engraving' ||
				category === 'Thread Gages',
			keyence_device: !isKeyenceStrategy(updated.measurement_strategy),
		};
		Object.entries(cleanableFields).forEach(([key, shouldClean]) => {
			if (shouldClean && updated[key as keyof Balloon] !== null) {
				updated[key as keyof Balloon] = null;
			}
		});
		return updated;
	};

	// Central update handler with automatic field cleaning
	const updateBalloonField = (id: string, updates: Partial<Balloon>) => {
		if (!editable) return;
		setValidationErrors((prevErrors) =>
			prevErrors.filter((error) => error.balloonId !== id)
		);
		setLocalBalloons(
			localBalloons.map((balloon: Balloon): Balloon => {
				if (balloon.id !== id) return balloon;

				const updated = markIndividualEdit(applyBalloonUpdates(balloon, updates));
				const errors = validateBalloon(updated);
				if (errors.length > 0) {
					setValidationErrors((prev) => [...prev, ...errors]);
				}

				return updated;
			})
		);
	};

	const validateBalloon = (balloon: Balloon): ValidationError[] => {
		// Save-time checks only. Tolerances are optional until completion (see missingFields).
		const errors: ValidationError[] = [];
		const requiredFields: (keyof Balloon)[] = [];

		if (!getDisabledStatus('measurement_strategy', balloon)) {
			requiredFields.push('measurement_strategy');
		}
		if (!getDisabledStatus('nominal', balloon)) {
			requiredFields.push('nominal');
		}

		requiredFields.forEach((field) => {
			const value = balloon[field];
			if (value === null || value === undefined || (value !== 0 && !value)) {
				errors.push({
					balloonId: balloon.id,
					field,
					message: `${field} is required`,
				});
			}
		});

		return errors;
	};

	const handleDeleteBalloon = (record: Balloon) => {
		if (!editable) return;
		const maxNumber = Math.max(...localBalloons.map((b) => b.balloon_number));


		if (disableDragging) {
			// In limited mode, allow gap creation - skip modal and renumbering
			performDeletion([record.id], false);
			return;
		}

		if (record.balloon_number < maxNumber) {
			setPendingDeletionRecord([record]);
			setConfirmModalVisible(true);
		} else {
			performDeletion([record.id], true);
		}
	};

	const performDeletion = (ids: React.Key[], renumber: boolean) => {
		const newBalloons = localBalloons.filter((b) => !ids.includes(b.id));
		setLocalBalloons(renumber ? renumberBalloons(newBalloons) : newBalloons);
		setSelectedRowKeys([]);

		const permanentIds = ids.filter((id) => !id.toString().startsWith('temp-'));
		if (permanentIds.length > 0) {
			setDeletedBalloonIds((prev: any) => [
				...prev,
				...permanentIds.map((id) => id.toString()),
			]);
		}
	};

	const handleDeleteSelected = () => {
		if (!editable) return;
		const selectedIds = selectedRowKeys;
		const maxNumber = Math.max(...localBalloons.map((b) => b.balloon_number));

		if (disableDragging) {
			// In limited mode, allow gap creation - skip modal and renumbering
			performDeletion(selectedIds, false);
			return;
		}

		const hasNonLastItems = localBalloons.some(
			(b) => selectedIds.includes(b.id) && b.balloon_number < maxNumber
		);

		if (hasNonLastItems) {
			setPendingDeletionRecord(
				localBalloons.filter((b) => selectedIds.includes(b.id))
			);
			setConfirmModalVisible(true);
		} else {
			performDeletion(selectedIds, true);
		}
	};

	const getOrderedBalloons = () => {
		return Object.values(localBalloons)
			.filter((b) => !needsReviewOnly || isUnreviewedAutoBalloon(b))
			.sort((a, b) => a.balloon_number - b.balloon_number);
	};

	const hasAutoGenerated = localBalloons.some((b) => b.is_auto_generated);
	const unreviewedCount = countUnreviewed(localBalloons);

	useEffect(() => {
		if (needsReviewOnly && unreviewedCount === 0) setNeedsReviewOnly(false);
	}, [needsReviewOnly, unreviewedCount]);

	const selectedUnreviewedIds = localBalloons
		.filter((b) => selectedRowKeys.includes(b.id) && isUnreviewedAutoBalloon(b))
		.map((b) => b.id as string);

	const approveBalloons = async (ids: string[]) => {
		if (!onApproveBalloons || ids.length === 0) return;
		setApprovingIds((prev) => [...prev, ...ids]);
		try {
			await onApproveBalloons(ids);
		} finally {
			setApprovingIds((prev) => prev.filter((id) => !ids.includes(id)));
		}
	};

	// Deliberately not a review: the rows keep their "Needs review" flag after the save.
	const handleBulkSetStrategy = (option: {
		value: string;
		keyenceId?: string;
	}) => {
		if (!editable || selectedRowKeys.length === 0) return;
		const updates: Partial<Balloon> = option.keyenceId
			? { measurement_strategy: 'Keyence', keyence_device: option.keyenceId }
			: { measurement_strategy: option.value, keyence_device: null };
		const targets = localBalloons.filter(
			(b) =>
				selectedRowKeys.includes(b.id) &&
				!getDisabledStatus('measurement_strategy', b)
		);
		const targetIds = targets.map((b) => b.id);
		const newErrors: ValidationError[] = [];
		setLocalBalloons(
			localBalloons.map((b) => {
				if (!targetIds.includes(b.id)) return b;
				const updated = markBulkEdit(applyBalloonUpdates(b, updates));
				newErrors.push(...validateBalloon(updated));
				return updated;
			})
		);
		setValidationErrors((prev) => [
			...prev.filter((e) => !targetIds.includes(e.balloonId)),
			...newErrors,
		]);
		message.success(
			`Strategy set to ${option.value} on ${targets.length} balloon${targets.length === 1 ? '' : 's'}.`
		);
	};

	// The tool picker's options depend on the strategy, so a batch needs one strategy.
	const selectedBalloons = localBalloons.filter((b) => selectedRowKeys.includes(b.id));
	const selectedStrategies = new Set(selectedBalloons.map((b) => b.measurement_strategy));
	const bulkTool =
		selectedBalloons.length > 0 && selectedStrategies.size === 1
			? toolField(selectedBalloons[0], fastlaneGages as any)
			: null;

	const handleBulkSetTool = (value: string) => {
		if (!editable || !bulkTool) return;
		const newErrors: ValidationError[] = [];
		const changedIds: string[] = [];
		const balloons = localBalloons.map((b) => {
			if (!selectedRowKeys.includes(b.id)) return b;
			const updates = toolUpdates(b, value, fastlaneGages as any);
			if (!updates) return b;
			const updated = markBulkEdit(applyBalloonUpdates(b, updates));
			newErrors.push(...validateBalloon(updated));
			changedIds.push(b.id);
			return updated;
		});
		setLocalBalloons(balloons);
		setValidationErrors((prev) => [
			...prev.filter((e) => !changedIds.includes(e.balloonId)),
			...newErrors,
		]);
		message.success(
			`${bulkTool.label} set on ${changedIds.length} balloon${changedIds.length === 1 ? '' : 's'}.`
		);
	};

	const strategyOptions = buildStrategyOptions(keyenceSystems);

	// Asks first when that could split the part's Keyence work across devices or affect
	// inspection data already recorded (only once the plan is in use).
	const handleStrategyChange = (record: Balloon, value: string, option: StrategyOption) => {
		const applyChange = () => updateBalloonField(record.id, strategyUpdates(value, option));

		const applyChangeWithSiblings = (siblingIds: string[]) => {
			applyChange();
			for (const sibId of siblingIds) {
				updateBalloonField(sibId, {
					measurement_strategy: 'Keyence',
					keyence_device: option.keyenceId,
				});
			}
		};

		if (allowedEditableBalloonId) {
			const wasKeyence = isKeyenceStrategy(record.measurement_strategy);
			const nowKeyence = !!option.keyenceId;
			const oldLabel = strategyValue(record, keyenceSystems);
			const newLabel = value;
			if (wasKeyence !== nowKeyence || (wasKeyence && nowKeyence && record.keyence_device !== option.keyenceId)) {
				// Keyence→Keyence device change: check for sibling balloons on the same old device
				if (wasKeyence && nowKeyence && record.keyence_device) {
					// Every Keyence balloon on the part, not just the ones on the
					// same old device: the part is inspected at one site, so they
					// all move together or none do (PTB-6816).
					const siblings = localBalloons.filter(
						(b) =>
							b.id !== record.id &&
							isKeyenceStrategy(b.measurement_strategy)
					);
					if (siblings.length > 0) {
						const siblingNums = siblings.map((b) => b.balloon_number).join(', ');
						Modal.confirm({
							title: 'Change Keyence device?',
							content: (
								<>
									<p>
										Changing balloon {record.balloon_number} from &quot;{oldLabel}&quot; to &quot;{newLabel}&quot;.
									</p>
									<p>
										Balloon(s) <strong>{siblingNums}</strong> will move to &quot;{newLabel}&quot; too.
										A part is inspected at one location, so all of its Keyence
										measurements have to use the same device.
									</p>
								</>
							),
							okText: 'Change all',
							okType: 'primary',
							cancelText: 'Cancel',
							onOk: () => applyChangeWithSiblings(siblings.map((b) => b.id)),
							// Cancel leaves everything as it was. There is deliberately
							// no "only this one": it would split the part across two
							// sites, which is the thing being prevented.
							onCancel: () => undefined,
						});
						return;
					}
				}
				Modal.confirm({
					title: 'Change measurement strategy?',
					content: `Changing balloon ${record.balloon_number} from "${oldLabel}" to "${newLabel}". Existing inspection data may be affected when saved.`,
					okText: 'Change',
					okType: 'danger',
					cancelText: 'Cancel',
					onOk: applyChange,
				});
				return;
			}
		}
		applyChange();
	};

	const handleToolChange = (record: Balloon, value: string) => {
		const updates = toolUpdates(record, value, fastlaneGages);
		if (updates) updateBalloonField(record.id, updates);
	};

	const handleCopyFromAbove = async (targetBalloonId: string) => {
		// 1. Get the target and source balloons
		const targetBalloon = localBalloons.find((b) => b.id === targetBalloonId)!;
		const sourceBalloon = localBalloons.find(
			(b) => b.balloon_number === targetBalloon.balloon_number - 1
		);

		if (!sourceBalloon) {
			message.warning('No balloon above to copy from.');
			return;
		}

		// 2. Prepare potential changes
		const potentialChanges: Partial<Balloon> = {};
		let willOverwriteExistingData = false;

		['nominal', 'tolerance_plus', 'tolerance_minus', 'text_value'].forEach(
			(field) => {
				const canCopy =
					!getDisabledStatus(field, sourceBalloon) &&
					!getDisabledStatus(field, targetBalloon);
				const sourceValue = sourceBalloon[field];
				const targetValue = targetBalloon[field];
				const hasSourceValue =
					field === 'text_value'
						? !!sourceValue?.trim()
						: sourceValue !== null && sourceValue !== undefined;

				if (canCopy && hasSourceValue) {
					// Check if it would overwrite existing non-empty values
					const willOverwrite =
						field === 'text_value'
							? !!targetValue?.trim()
							: targetValue !== null && targetValue !== undefined;

					if (willOverwrite) {
						willOverwriteExistingData = true;
					}

					potentialChanges[field] = sourceValue;
				}
			}
		);
		if ('tolerance_plus' in potentialChanges || 'tolerance_minus' in potentialChanges) {
			potentialChanges.tolerance_source = sourceBalloon.tolerance_source ?? null;
		}

		// 3. If changes would overwrite existing data, ask for confirmation
		const applyChanges = async () => {
			const updatedBalloons = localBalloons.map((b) =>
				b.id === targetBalloonId
					? markIndividualEdit({ ...b, ...potentialChanges })
					: b
			);
			setLocalBalloons(updatedBalloons);
			message.success('Values copied from the balloon above.');
		};

		if (Object.keys(potentialChanges).length === 0) {
			message.info('Nothing to copy.');
			return;
		}

		if (willOverwriteExistingData) {
			Modal.confirm({
				title: 'Confirm overwrite',
				content:
					'Some fields already have values. Do you want to overwrite them with values from the balloon above?',
				okText: 'Yes, overwrite',
				cancelText: 'Cancel',
				onOk: applyChanges,
				okButtonProps: {
					style: {
						background: '#1890ff',
						borderColor: '#1890ff',
					},
				},
			});
		} else {
			applyChanges();
		}
	};

	const getDisabledStatus = (
		field: keyof Balloon,
		record: Balloon
	): boolean => {
		if (allowedEditableBalloonId) {
			if (allowedEditableBalloonId === 'NONE') return true;
			if (record.id !== allowedEditableBalloonId) return true;
		}

		const measurementFields: string[] = [
			'view',
			'measurement_type',
			'gage_type',
			'text_value',
			'nominal',
			'tolerance_minus',
			'tolerance_plus',
		];
		return measurementFields.includes(field as string)
			? isFieldDisabled(field as MeasurementField, record, gageTypeMap as any)
			: false;
	};

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.shiftKey) isShiftPressed.current = true;
		};
		const handleKeyUp = (e: KeyboardEvent) => {
			if (!e.shiftKey) {
				isShiftPressed.current = false;
				selectionAnchorRef.current = null;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	const getRangeSelection = useCallback(
		(startId: string, endId: string) => {
			const startIndex = localBalloons.findIndex((b) => b.id === startId);
			const endIndex = localBalloons.findIndex((b) => b.id === endId);
			if (startIndex === -1 || endIndex === -1) return [];

			const [from, to] = [
				Math.min(startIndex, endIndex),
				Math.max(startIndex, endIndex),
			];
			return localBalloons.slice(from, to + 1).map((b) => b.id);
		},
		[localBalloons]
	);

	const handleRowKeyDown = useCallback(
		(record: Balloon, event: React.KeyboardEvent) => {
			if (focusedSelectId === record.id) {
				event.stopPropagation();
				return;
			}

			if (
				event.shiftKey &&
				(event.key === 'ArrowUp' || event.key === 'ArrowDown')
			) {
				event.preventDefault();

				const currentId = activeBalloonId || record.id;
				const currentIndex = localBalloons.findIndex((b) => b.id === currentId);
				const direction = event.key === 'ArrowUp' ? -1 : 1;
				const newIndex = currentIndex + direction;

				if (newIndex >= 0 && newIndex < localBalloons.length) {
					const newRecord = localBalloons[newIndex];

					if (event.shiftKey) {
						if (!selectionAnchorRef.current)
							selectionAnchorRef.current = currentId;
						setSelectedRowKeys(
							getRangeSelection(selectionAnchorRef.current, newRecord.id)
						);
					} else {
						setSelectedRowKeys([newRecord.id]);
						selectionAnchorRef.current = newRecord.id;
					}

					setActiveBalloonId(newRecord.id);
					setCenterOnActive(true);
					setPageNumber(newRecord.ballooning_page_number);
				}
			}

			// Normal navigation mode (without Shift)
			if (
				!event.shiftKey &&
				['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
			) {
				event.preventDefault();

				const currentRowIndex = localBalloons.findIndex(
					(b) => b.id === record.id
				);
				const columnKeys = ['strategy', 'tool', 'nominal', 'minus', 'plus'];
				const currentColumnIndex = columnKeys.indexOf(
					focusedCell?.columnKey || 'strategy'
				);

				let newRowIndex = currentRowIndex;
				let newColumnIndex = currentColumnIndex;

				if (event.key === 'ArrowUp') {
					newRowIndex = Math.max(0, currentRowIndex - 1);
				} else if (event.key === 'ArrowDown') {
					newRowIndex = Math.min(localBalloons.length - 1, currentRowIndex + 1);
				} else if (event.key === 'ArrowLeft') {
					newColumnIndex = Math.max(0, currentColumnIndex - 1);
				} else if (event.key === 'ArrowRight') {
					newColumnIndex = Math.min(
						columnKeys.length - 1,
						currentColumnIndex + 1
					);
				}

				const newRowId = localBalloons[newRowIndex]?.id;
				const newColumnKey = columnKeys[newColumnIndex];

				if (newRowId && newColumnKey) {
					setFocusedCell({ rowId: newRowId, columnKey: newColumnKey });

					const targetElement = cellRefs.current[newRowId]?.[newColumnKey];

					if (targetElement) {
						// Try to focus the element first
						targetElement.focus();

						if (
							targetElement instanceof HTMLInputElement ||
							targetElement instanceof HTMLTextAreaElement
						) {
							targetElement.select();
						}
					}

					setSelectedRowKeys([newRowId]);
					selectionAnchorRef.current = newRowId;

					setActiveBalloonId(newRowId);
					setCenterOnActive(true);
					setPageNumber(localBalloons[newRowIndex].ballooning_page_number);
				}
			}
		},
		[
			focusedCell,
			focusedSelectId,
			getRangeSelection,
			localBalloons,
			activeBalloonId,
			setActiveBalloonId,
			setCenterOnActive,
			setPageNumber,
		]
	);

	const handleRowClick = useCallback(
		(record: Balloon, event: React.MouseEvent) => {
			const target = event.target as HTMLElement;
			if (target.closest('[data-no-center]')) return;

			let newSelection: React.Key[] = [];
			if (isShiftPressed.current && selectionAnchorRef.current) {
				newSelection = getRangeSelection(selectionAnchorRef.current, record.id);
			} else {
				newSelection = [record.id];
				selectionAnchorRef.current = record.id;
			}

			setSelectedRowKeys(newSelection);
			setActiveBalloonId(record.id);
			setCenterOnActive(true);
			setPageNumber(record.ballooning_page_number);
		},
		[setActiveBalloonId, setCenterOnActive, setPageNumber, getRangeSelection]
	);

	const handleCellFocus = (rowId: string, columnKey: string) => {
		setFocusedCell({ rowId, columnKey });
		setActiveBalloonId(rowId);
		setCenterOnActive(true);
	};

	const handleDuplicateSelected = useCallback(() => {
		if (!editable) return;
		if (selectedRowKeys.length === 0) return;

		const selectedBalloons = localBalloons.filter((b) =>
			selectedRowKeys.includes(b.id)
		);
		const maxBalloonNumber = Math.max(
			...selectedBalloons.map((b) => b.balloon_number)
		);

		const duplicated = selectedBalloons.map((b, i) => ({
			...stripAutoGeneration(b),
			id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
			balloon_number: maxBalloonNumber + i + 1,
		}));

		const lastSelectedIndex = localBalloons.findIndex(
			(b) => b.balloon_number === maxBalloonNumber
		);

		let newBalloons: Balloon[];

		if (disableDragging) {
			// In limited mode, append to the end to prevent renumbering existing balloons
			newBalloons = [...localBalloons, ...duplicated];
		} else {
			// Normal mode: insert after the selection
			newBalloons = [
				...localBalloons.slice(0, lastSelectedIndex + 1),
				...duplicated,
				...localBalloons.slice(lastSelectedIndex + 1),
			];
		}

		setLocalBalloons(renumberBalloons(newBalloons));
		setSelectedRowKeys(duplicated.map((d) => d.id));
	}, [selectedRowKeys, localBalloons, setLocalBalloons]);

	useEffect(() => {
		const handleKeyPress = (e: KeyboardEvent) => {
			// Duplicate: Ctrl/Cmd + D
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
				e.preventDefault();
				handleDuplicateSelected();
			}

			// Delete: Ctrl/Cmd + Delete
			if (
				(e.ctrlKey || e.metaKey) &&
				(e.key === 'Delete' || e.key === 'Backspace')
			) {
				e.preventDefault();
				handleDeleteSelected();
			}

			// Deseleccionar: ESC
			if (e.key === 'Escape' && selectedRowKeys.length > 0) {
				e.preventDefault();
				setSelectedRowKeys([]);
			}

			// Copy from above: Ctrl/Cmd + Shift + ArrowRight
			if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'ArrowRight') {
				e.preventDefault();
				if (selectedRowKeys.length === 1) {
					handleCopyFromAbove(selectedRowKeys[0]);
				} else {
					message.warning('Please select a single row to copy from above.');
				}
			}
		};

		document.addEventListener('keydown', handleKeyPress);
		return () => document.removeEventListener('keydown', handleKeyPress);
	}, [handleDuplicateSelected, handleDeleteSelected]);

	const rowSelection = {
		selectedRowKeys,
		onChange: (selectedKeys: React.Key[]) => setSelectedRowKeys(selectedKeys),
		getCheckboxProps: () => ({ tabIndex: -1, disabled: !editable }),
		selections: editable
			? [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE]
			: undefined,
	};

	const handleDrop = async (dragged: Balloon, target: Balloon) => {
		if (!editable || disableDragging) return;
		const draggedIndex = localBalloons.findIndex((b) => b.id === dragged.id);
		const targetIndex = localBalloons.findIndex((b) => b.id === target.id);

		if (draggedIndex === -1 || targetIndex === -1) return;

		// Create a temporary reordered array for visual feedback
		const tempBalloons = [...localBalloons];
		const [removed] = tempBalloons.splice(draggedIndex, 1);
		tempBalloons.splice(targetIndex, 0, removed);

		// Check if current numbering has gaps
		const hasSequenceGaps = () => {
			const numbers = localBalloons
				.map((b) => b.balloon_number)
				.sort((a, b) => a - b);
			for (let i = 0; i < numbers.length; i++) {
				if (numbers[i] !== i + 1) return true;
			}
			return false;
		};

		// Show visual movement immediately (but don't update state yet)
		setLocalBalloons(tempBalloons);

		try {
			if (hasSequenceGaps()) {
				await Modal.confirm({
					title: 'Non-Sequential Balloon Numbers Detected',
					content:
						'The current balloon numbering has gaps. Renumber all balloons sequentially?',
					okText: 'Renumber All',
					cancelText: 'Cancel Move',
					onOk: () => {
						// User confirmed - proceed with sequential renumbering
						const renumbered = tempBalloons.map((balloon, index) => ({
							...balloon,
							balloon_number: index + 1,
						}));
						setLocalBalloons(renumbered);
					},
					onCancel: () => {
						// User cancelled - revert to original order
						setLocalBalloons([...localBalloons]);
					},
					okButtonProps: {
						style: {
							background: '#1890ff',
							borderColor: '#1890ff',
						},
					},
				});
			} else {
				// No gaps - just do the sequential renumbering
				const renumbered = tempBalloons.map((balloon, index) => ({
					...balloon,
					balloon_number: index + 1,
				}));
				setLocalBalloons(renumbered);
			}
		} catch {
			// If modal is dismissed, revert to original order
			setLocalBalloons([...localBalloons]);
		}
	};

	const onRow = (record: Balloon) => ({
		...props.onRow?.(record),
		onClick: (e: React.MouseEvent) => handleRowClick(record, e),
		onKeyDown: (e: React.KeyboardEvent) => handleRowKeyDown(record, e),
		onFocus: () => {
			if (lastInteractionWasKeyboard.current) {
				setSelectedRowKeys([record.id]);
			}
		},
		tabIndex: -1,
		style: { cursor: editable && !disableDragging ? 'move' : 'default' },
		draggable: editable && !disableDragging,
		onDragStart: () => {
			setDraggedRow(record);
			setSelectedRowKeys([record.id]);
		},
		onDragEnter: () => setHoveredRow(record.id),
		onDragOver: (e: React.DragEvent) => e.preventDefault(),
		onDragEnd: () => {
			setDraggedRow(null);
			setHoveredRow(null);
		},
		onDrop: (e: React.DragEvent) => {
			e.preventDefault();
			if (draggedRow && draggedRow.id !== record.id) {
				handleDrop(draggedRow, record);
			}
			setHoveredRow(null);
		},
	});

	const rowClassName = useCallback(
		(record: Balloon) => {
			let className = `${record.id === activeBalloonId ? 'active-row' : ''} ${record.id?.startsWith('temp-') ? 'duplicated-row' : ''
				}`;

			if (isUnreviewedAutoBalloon(record)) {
				className += ' needs-review-row';
			}

			if (record.id === draggedRow?.id) {
				className += ' dragging-row';
			}

			if (record.id === hoveredRow) {
				className += ' drop-target';
				const draggedIndex = localBalloons.findIndex(
					(b) => b.id === draggedRow?.id
				);
				const targetIndex = localBalloons.findIndex((b) => b.id === record.id);

				if (draggedIndex !== -1 && targetIndex !== -1) {
					if (targetIndex > draggedIndex) {
						className += ' drop-target-above';
					} else {
						className += ' drop-target-below';
					}
				}
			}

			return className;
		},
		[activeBalloonId, draggedRow, hoveredRow, localBalloons]
	);

	const getToleranceHint = (): string | null =>
		hasRows(toleranceTable) ? 'Tolerance table' : null;

	const getFullToleranceDescription = (): string => {
		if (!hasRows(toleranceTable)) return 'No tolerance table';
		return [
			`Tolerance table (${TABLE_SOURCE_LABELS[toleranceTable.source]})`,
			...toleranceTable.rows.map((row) => `• ${describeRow(row)}`),
			'Typing a nominal fills its tolerance from this table.',
		].join('\n');
	};

	const countDecimals = (val: string | number | null | undefined): number => {
		if (val === null || val === undefined) return 0;
		const s = String(val);
		const dot = s.indexOf('.');
		return dot === -1 ? 0 : s.length - dot - 1;
	};

	const trimTrailingZeros = (s: string): string => s.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');

	const formatControlLimit = (
		a: string | number | null | undefined,
		b: string | number | null | undefined,
		op: 'add' | 'sub'
	): string | null => {
		if (a === null || a === undefined) return null;
		const dec = Math.max(countDecimals(a), countDecimals(b ?? 0));
		const scale = Math.pow(10, dec);
		const ai = Math.round(Number(a) * scale);
		const bi = Math.round(Number(b ?? 0) * scale);
		const ri = op === 'add' ? ai + bi : ai - bi;
		const fixed = (ri / scale).toFixed(dec);
		return trimTrailingZeros(fixed);
	};

	// Table columns configuration
	const columns = [
		{
			title: '',
			width: 40,
			fixed: 'left' as const,
			render: (record: Balloon) => <MenuOutlined />,
		},
		{
			title: 'ID',
			width: 30,
			render: (record: Balloon) => record.balloon_number,
		},
		...(hasAutoGenerated
			? [
				{
					title: 'Review',
					width: 140,
					render: (record: Balloon) => {
						if (!record.is_auto_generated) return null;
						const unreviewed = isUnreviewedAutoBalloon(record);
						const confidence =
							typeof record.ai_confidence === 'number'
								? `${Math.round(record.ai_confidence * 100)}%`
								: 'n/a';
						const reviewedLine = record.is_reviewed
							? `Reviewed${record.reviewed_by_name ? ` by ${record.reviewed_by_name}` : ''}${record.reviewed_at ? ` on ${new Date(record.reviewed_at).toLocaleString()}` : ''}`
							: 'Not reviewed yet. Approve it, or edit a measurement field and save.';
						return (
							<Space size={4} wrap={false}>
								<Tooltip
									title={
										<div style={{ whiteSpace: 'pre-line' }}>
											{`Auto-generated\nDetected text: ${record.ai_raw_text || 'n/a'}\nConfidence: ${confidence}\n${record.ai_strategy_reason ? `Strategy: ${record.ai_strategy_reason}\n` : ''}${reviewedLine}`}
										</div>
									}
								>
									<Tag
										icon={unreviewed ? <OpenAIOutlined /> : <CheckOutlined />}
										color={unreviewed ? 'orange' : 'green'}
										style={{ marginInlineEnd: 0 }}
										data-testid={`balloon-review-tag-${record.balloon_number}`}
									>
										{unreviewed ? 'Needs review' : 'Reviewed'}
									</Tag>
								</Tooltip>
								{unreviewed && onApproveBalloons && (
									<Tooltip title="Mark as reviewed without changing it">
										<Button
											size="small"
											type="text"
											icon={<CheckOutlined style={{ color: '#52c41a' }} />}
											loading={approvingIds.includes(record.id)}
											onClick={(e) => {
												e.stopPropagation();
												approveBalloons([record.id]);
											}}
											tabIndex={-1}
											aria-label={`Approve balloon ${record.balloon_number}`}
										/>
									</Tooltip>
								)}
							</Space>
						);
					},
				},
			]
			: []),
		{
			title: 'Strategy',
			width: 100,
			render: (record: Balloon) => (
				<Select
					size="small"
					variant="borderless"
					style={{
						width: '100%',
						border: !record.measurement_strategy ? '1px solid #ff4d4f' : 'none',
					}}
					value={strategyValue(record, keyenceSystems)}
					options={strategyOptions}
					onChange={(value, option) =>
						handleStrategyChange(record, value, option as StrategyOption)
					}
					showSearch
					onFocus={() => setFocusedSelectId(record.id)}
					onBlur={() => setFocusedSelectId(null)}
					onKeyDown={(e) => {
						e.stopPropagation();
						if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
							e.nativeEvent.stopImmediatePropagation();
						}
					}}
					disabled={getDisabledStatus('id', record) || !editable}
				/>
			),
		},
		{
			title: 'Tool',
			width: 240,
			render: (record: Balloon) => {
				const tool = toolField(record, fastlaneGages as any);
				const options = tool?.options ?? [];
				const value = tool?.value ?? '';
				const placeholder = tool?.placeholder ?? 'Select...';

				return (
					<Select
						ref={(el) => registerCellRef(record.id, 'tool', el)}
						value={value}
						size="small"
						variant="borderless"
						style={{
							width: '100%',
							border: !value ? '1px solid #ff4d4f' : 'none',
						}}
						placeholder={placeholder}
						onFocus={() => {
							handleCellFocus(record.id, 'tool');
							setFocusedSelectId(record.id);
						}}
						onBlur={() => setFocusedSelectId(null)}
						onKeyDown={(e) => {
							e.stopPropagation();
							if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
								e.nativeEvent.stopImmediatePropagation();
							}
						}}
						onChange={(selectedValue) => handleToolChange(record, selectedValue)}
						options={options}
						showSearch
						optionFilterProp="label"
						filterOption={(input, option) =>
							String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
						}
						disabled={getDisabledStatus('id', record) || !editable}
					/>
				);
			},
		},
		{
			title: 'Nominal/Description',
			width: 120,
			render: (record: Balloon) => {
				const showNominal = !getDisabledStatus('nominal', record);
				const showDescription =
					!getDisabledStatus('text_value', record) && !showNominal;

				const hasNominalError = validationErrors.some(
					(e) => e.balloonId === record.id && e.field === 'nominal'
				);
				const hasDescriptionError = validationErrors.some(
					(e) => e.balloonId === record.id && e.field === 'text_value'
				);
				const handleNominalChange = (value: string | null) => {
					if (!editable) return;

					const updatedBalloons = localBalloons.map((balloon) =>
						balloon.id === record.id
							? applyNominalEdit(balloon, value, toleranceTable)
							: balloon
					);

					setLocalBalloons(updatedBalloons);
				};

				const toleranceHint = getToleranceHint();
				const hasTolerance = toleranceHint !== null;

				return (
					<div style={{ width: '100%' }}>
						{showNominal ? (
							<>
								<NominalInput
									ref={(el) => registerCellRef(record.id, 'nominal', el)}
									onFocus={() => handleCellFocus(record.id, 'nominal')}
									size="small"
									variant="borderless"
									style={{
										width: '100%',
										border: hasNominalError ? '1px solid #ff4d4f' : 'none',
									}}
									placeholder="Nominal"
									nominal={record.nominal}
									decimals={record.nominal_decimals}
									readOnly={getDisabledStatus('nominal', record) || !editable}
									tabIndex={getDisabledStatus('nominal', record) ? -1 : undefined}
									onChange={handleNominalChange}
								/>

								{hasTolerance && (
									<Tooltip
										title={getFullToleranceDescription()}
										placement="bottom"
										overlayStyle={{ maxWidth: '500px', whiteSpace: 'pre-wrap' }}
									>
										<span
											style={{
												position: 'absolute',
												right: '8px',
												top: '50%',
												transform: 'translateY(-50%)',
												color: '#1890ff',
												cursor: 'pointer',
											}}
										>
											<InfoCircleOutlined />
										</span>
									</Tooltip>
								)}
							</>
						) : showDescription ? (
							<Input
								ref={(el) => registerCellRef(record.id, 'nominal', el)}
								onFocus={() => handleCellFocus(record.id, 'nominal')}
								size="small"
								variant="borderless"
								placeholder="Description"
								style={{
									width: '100%',
									border: hasDescriptionError ? '1px solid #ff4d4f' : 'none',
								}}
								keyboard={false}
								value={record.text_value || ''}
								readOnly={getDisabledStatus('text_value', record) || !editable}
								tabIndex={
									getDisabledStatus('text_value', record) ? -1 : undefined
								}
								onChange={(e) =>
									updateBalloonField(record.id, { text_value: e.target.value })
								}
							/>
						) : (
							<Tooltip title={allowedEditableBalloonId ? 'Please save your changes before editing other balloons' : undefined}>
								<span style={{ color: '#999', fontSize: 12, cursor: allowedEditableBalloonId ? 'not-allowed' : 'default' }}>
									{record.nominal || record.text_value || '—'}
								</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			title: 'Minus',
			width: 60,
			render: (record: Balloon) => (
				<InputNumber
					ref={(el) => registerCellRef(record.id, 'minus', el)}
					onFocus={() => handleCellFocus(record.id, 'minus')}
					size="small"
					variant="borderless"
					style={{
						width: '100%',
						border: validationErrors.some(
							(e) => e.balloonId === record.id && e.field === 'tolerance_minus'
						)
							? '1px solid red'
							: 'none',
					}}
					controls={false}
					keyboard={false}
					value={record.tolerance_minus}
					readOnly={getDisabledStatus('tolerance_minus', record) || !editable}
					tabIndex={
						getDisabledStatus('tolerance_minus', record) ? -1 : undefined
					}
					onChange={(value) =>
						updateBalloonField(record.id, {
							tolerance_minus: value,
							tolerance_source: 'manual',
						})
					}
				/>
			),
		},
		{
			title: 'Plus',
			width: 60,
			render: (record: Balloon) => (
				<InputNumber
					ref={(el) => registerCellRef(record.id, 'plus', el)}
					onFocus={() => handleCellFocus(record.id, 'plus')}
					size="small"
					variant="borderless"
					style={{
						width: '100%',
						border: validationErrors.some(
							(e) => e.balloonId === record.id && e.field === 'tolerance_plus'
						)
							? '1px solid red'
							: 'none',
					}}
					controls={false}
					keyboard={false}
					value={record.tolerance_plus}
					readOnly={getDisabledStatus('tolerance_plus', record) || !editable}
					tabIndex={
						getDisabledStatus('tolerance_plus', record) ? -1 : undefined
					}
					onChange={(value) =>
						updateBalloonField(record.id, {
							tolerance_plus: value,
							tolerance_source: 'manual',
						})
					}
				/>
			),
		},
		{
			title: 'LCL',
			width: 50,
			align: 'center',
			render: (record: Balloon) => {
				const value = formatControlLimit(
					record.nominal,
					record.tolerance_minus,
					'sub'
				);
				return value !== null ? (
					<span style={{ whiteSpace: 'nowrap' }}>{value}</span>
				) : null;
			},
		},
		{
			title: 'UCL',
			width: 50,
			align: 'center',
			render: (record: Balloon) => {
				const value = formatControlLimit(
					record.nominal,
					record.tolerance_plus,
					'add'
				);
				return value !== null ? (
					<span style={{ whiteSpace: 'nowrap' }}>{value}</span>
				) : null;
			},
		},
		{
			align: 'right',
			width: 50,
			render: (record: Balloon) => {
				const sortedBalloons = [...localBalloons].sort(
					(a, b) => a.balloon_number - b.balloon_number
				);
				const isFirst =
					sortedBalloons[0]?.balloon_number === record.balloon_number;

				const hasEditableFields = [
					'nominal',
					'tolerance_plus',
					'tolerance_minus',
					'text_value',
				].some((field) => !getDisabledStatus(field as keyof Balloon, record));

				return (
					<Space>
						<Tooltip
							title={
								isFirst
									? 'Cannot copy from the first balloon'
									: !hasEditableFields
										? 'No editable fields available to copy'
										: !editable
											? 'Editing is disabled'
											: selectedRowKeys.length > 1
												? 'Select only one row to copy from above'
												: 'Click to copy data from the balloon above (Ctrl/Cmd + Shift + ArrowRight)'
							}
						>
							<Button
								icon={<VerticalAlignBottomOutlined />}
								size="small"
								onClick={() => handleCopyFromAbove(record.id)}
								disabled={
									isFirst ||
									!hasEditableFields ||
									!editable ||
									selectedRowKeys.length > 1
								}
								tabIndex={-1}
							/>
						</Tooltip>

						<Popconfirm
							title={`Delete balloon ${record.balloon_number}?`}
							onConfirm={() => handleDeleteBalloon(record)}
							okText="Delete"
							okButtonProps={{ danger: true }}
							disabled={getDisabledStatus('id', record) || !editable}
						>
							<Button
								icon={<DeleteOutlined />}
								size="small"
								tabIndex={-1}
								disabled={getDisabledStatus('id', record) || !editable}
							/>
						</Popconfirm>
					</Space>
				);
			},
		},
	];

	return (
		<div className="flex h-full flex-col">
			<style>
				{`
					:root {
						--primary-bg: #f0f8ff; /* Azul claro muy suave */
						--primary-border: #4dabf7; /* Azul profesional */
						--cell-highlight: #e3f2fd; /* Azul pastel */
					}

					/* Zebra striping */
					.ant-table-tbody > tr:nth-child(even) {
						background: #f8f9fa !important; /* Gris muy claro */
					}

					/* Resaltado de fila */
					.ant-table-row:focus-within {
						background: var(--primary-bg) !important;
						box-shadow: inset 0 0 0 2px var(--primary-border) !important;
					}

					/* Resaltado de celda */
					.ant-table-row td:focus-within {
						background: var(--cell-highlight) !important;
						box-shadow: inset 0 0 0 2px var(--primary-border) !important;
					}

					/* Transición suave */
					.ant-table-row, .ant-table-row td {
						transition: all 0.3s ease;
					}

					.duplicated-row {
						background-color: #f6ffed !important;
						transition: background-color 0.3s ease;
					}

					.ant-table td.ant-table-cell {
						padding: 8px !important;
						min-width: 150px;
					}

					.dragging-row {
						opacity: 0.5;
						background: #f0f0f0;
					}

					.drop-target-above {
						border-top: 2px solid #1890ff;
					}

					.drop-target-below {
						border-bottom: 2px solid #1890ff;
					}

					.ant-table-row.drop-target {
						background-color: #e6f7ff !important;
					}

					.ant-table-tbody > tr.needs-review-row > td:first-child {
						box-shadow: inset 3px 0 0 #fa8c16;
					}
					.ant-table-tbody > tr.needs-review-row {
						background-color: #fff7e6 !important;
					}
		 			.non-editable-table .ant-table-row {
                        cursor: not-allowed !important;
                        background-color: #f5f5f5 !important;
                    }
                    .non-editable-table .ant-table-cell {
                        pointer-events: none !important;
                    }

				`}
			</style>
			<div className="flex-none" style={{ paddingBottom: 8 }}>
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
					}}
				>
					{toolbarLeft && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								marginTop: 8,
								marginRight: 8,
							}}
						>
							{toolbarLeft}
						</div>
					)}
					{hasAutoGenerated && (
						<Space style={{ marginTop: 8, marginRight: 'auto', marginLeft: 8 }}>
							<Switch
								size="small"
								checked={needsReviewOnly}
								onChange={setNeedsReviewOnly}
								disabled={unreviewedCount === 0}
								data-testid="balloon-needs-review-filter"
							/>
							<span>
								Needs review only ({unreviewedCount})
							</span>
						</Space>
					)}
					{hasAutoGenerated && onApproveBalloons && (
						<Button
							size="small"
							icon={<CheckOutlined />}
							onClick={() => approveBalloons(selectedUnreviewedIds)}
							disabled={selectedUnreviewedIds.length === 0}
							loading={
								selectedUnreviewedIds.length > 0 &&
								selectedUnreviewedIds.every((id) => approvingIds.includes(id))
							}
							style={{ marginTop: 8, marginRight: 8 }}
						>
							Approve selected
							{selectedUnreviewedIds.length > 0 ? ` (${selectedUnreviewedIds.length})` : ''}
						</Button>
					)}
					<Tooltip
						title="Set the strategy on every selected balloon. This does not count as reviewing them."
						placement="left"
					>
						<Select
							size="small"
							placeholder="Set strategy"
							value={null}
							options={strategyOptions}
							onChange={(_value, option: any) => handleBulkSetStrategy(option)}
							disabled={
								!!allowedEditableBalloonId ||
								selectedRowKeys.length === 0 ||
								!editable
							}
							popupMatchSelectWidth={false}
							style={{ width: 130, marginTop: 8, marginRight: 8 }}
							data-testid="balloon-bulk-strategy"
						/>
					</Tooltip>
					<Tooltip
						title={
							selectedRowKeys.length > 0 && !bulkTool
								? 'Select balloons that share one strategy to set their tool.'
								: 'Set the view, gage or visual type on every selected balloon. This does not count as reviewing them.'
						}
						placement="left"
					>
						<Select
							size="small"
							placeholder={bulkTool ? `Set ${bulkTool.label.toLowerCase()}` : 'Set tool'}
							value={null as string | null}
							options={bulkTool?.options ?? []}
							onChange={(value) => value && handleBulkSetTool(value)}
							disabled={!!allowedEditableBalloonId || !bulkTool || !editable}
							showSearch
							optionFilterProp="label"
							popupMatchSelectWidth={false}
							style={{ width: 130, marginTop: 8, marginRight: 8 }}
							data-testid="balloon-bulk-tool"
						/>
					</Tooltip>
					<Tooltip title="Duplicate selection (Ctrl/Cmd + D)" placement="left">
						<Button
							size="small"
							type="primary"
							icon={<CopyOutlined />}
							onClick={handleDuplicateSelected}
							disabled={
								!!allowedEditableBalloonId ||
								selectedRowKeys.length === 0 ||
								!editable
							}
							style={{
								marginTop: 8,
								marginRight: 8,
								transition: 'all 0.3s',
							}}
						>
							Duplicate
						</Button>
					</Tooltip>

					<Tooltip
						title="Delete selection (Ctrl/Cmd + Delete)"
						placement="left"
					>
						<Button
							size="small"
							danger
							type="primary"
							icon={<DeleteOutlined />}
							onClick={handleDeleteSelected}
							disabled={
								!!allowedEditableBalloonId ||
								selectedRowKeys.length === 0 ||
								!editable
							}
							style={{ marginRight: 8, marginTop: 8 }}
						>
							Delete
						</Button>
					</Tooltip>
				</div>
			</div>
			<div ref={tableAreaRef} className="min-h-0 flex-1 overflow-hidden">
				<Table
					columns={columns}
					dataSource={getOrderedBalloons()}
					size="small"
					pagination={false}
					scroll={{ x: '720px', y: tableBodyHeight ?? (scrollY ? scrollY - 40 : undefined) }}
					rowKey="id"
					rowSelection={rowSelection}
					rowClassName={rowClassName}
					onRow={onRow}
				/>
			</div>

			<DeleteBalloonsModal
				visible={confirmModalVisible}
				onConfirm={(renumber: boolean) => {
					performDeletion(
						pendingDeletionRecord.map((b) => b.id),
						renumber
					);
					setConfirmModalVisible(false);
				}}
				onCancel={() => {
					setConfirmModalVisible(false);
				}}
				pendingDeletionRecord={pendingDeletionRecord}
				deletesAll={localBalloons.every((b) =>
					pendingDeletionRecord.some((p) => p.id === b.id)
				)}
			/>
		</div>
	);
});

export default BalloonTable;
